import React, { useState } from 'react';
import { Search, ChevronDown, ChevronRight, ArrowDown, ArrowUp, Check } from 'lucide-react';
import { useIndustry } from '../context/IndustryContext';
import { getIndustryData } from '../data/industryDataService';
import Walkthrough from '../components/Walkthrough';

const walkthroughSteps = [
  { target: '[data-testid="dashboard-header"]', title: 'Teams', description: 'CRO view, see your entire team\'s performance at a glance. Pipeline ownership, task completion rates, and how many accounts are at risk per rep. Spot coaching opportunities and workload imbalances instantly.' },
  { target: '[data-testid="teams-search"]', title: 'Search & Filter', description: 'Find team members by name and filter by role, Rep (Account Executive), CSM (Customer Success Manager), or Manager.' },
  { target: '[data-testid="teams-columns"]', title: 'Member', description: 'The team member\'s name and their assigned region. Quickly see who covers which territory.' },
  { target: '[data-testid="teams-columns"]', title: 'Role', description: 'The member\'s function, Rep, CSM, or Manager. Color-coded pills for quick identification.' },
  { target: '[data-testid="teams-columns"]', title: 'Pipeline', description: 'Total ARR value of all deals assigned to this team member. Indicates their current book of business.' },
  { target: '[data-testid="teams-columns"]', title: 'Action Rate', description: 'Percentage of assigned tasks completed, with a trend arrow, up means improving, down means declining. Spot coaching opportunities.' },
  { target: '[data-testid="teams-columns"]', title: 'At Risk', description: 'Number of flagged accounts needing attention. Higher numbers signal a rep who may be overloaded or needs support.' },
  { target: '[data-testid="team-row-1"]', title: 'Member Details', description: 'Review each rep\'s workload, completion rate, and risk exposure to identify who needs coaching or support.' },
];

const rolePillColors = {
  'Rep': 'bg-green-100 text-green-700',
  'CSM': 'bg-blue-100 text-blue-700',
  'Manager': 'bg-purple-100 text-purple-700',
};

export default function Teams() {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('All Roles');
  const { selectedIndustry } = useIndustry();
  const { teamMembers } = getIndustryData(selectedIndustry);

  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'All Roles' || member.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <div className="space-y-6" data-testid="teams-page">
      <Walkthrough steps={walkthroughSteps} pageKey="teams" />
      {/* Main Card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="teams-card">
        {/* Header */}
        <div className="px-6 py-5 flex items-center justify-between gap-4">
          <h2 className="text-lg font-bold text-gray-900">Team Performance</h2>
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative" data-testid="teams-search">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search team..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-[180px]"
              />
            </div>
            {/* Role Filter */}
            <div className="relative" data-testid="teams-role-filter">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="appearance-none flex items-center gap-1.5 px-3 py-2 pr-8 text-sm border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option>All Roles</option>
                <option>Rep</option>
                <option>CSM</option>
                <option>Manager</option>
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead data-testid="teams-columns">
              <tr className="border-t border-gray-100">
                <th className="text-left text-xs font-medium text-gray-500 px-6 py-3 w-[28%]">Member</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Role</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Pipeline</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Action Rate</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">At Risk</th>
                <th className="w-10 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((member) => (
                <tr key={member.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors cursor-pointer" data-testid={`team-row-${member.id}`}>
                  {/* Member */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full ${member.avatarColor} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                        {member.avatar}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{member.name}</p>
                        <p className="text-xs text-gray-500">{member.region}</p>
                      </div>
                    </div>
                  </td>
                  {/* Role */}
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${rolePillColors[member.role] || 'bg-gray-100 text-gray-600'}`}>
                      {member.role}
                    </span>
                  </td>
                  {/* Pipeline */}
                  <td className="px-4 py-4">
                    {member.pipeline ? (
                      <div>
                        <p className="text-sm font-bold text-gray-900">${Math.round(member.pipeline / 1000)}K</p>
                        <p className="text-xs text-gray-500">{member.dealsCount || member.reportsCount} {member.dealsCount ? 'deals' : 'reports'}</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm font-bold text-gray-400">&mdash;</p>
                        <p className="text-xs text-gray-500">{member.reportsCount} reports</p>
                      </div>
                    )}
                  </td>
                  {/* Action Rate */}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium text-gray-900">{member.actionRate}%</span>
                      {member.actionTrend === 'up' ? (
                        <ArrowUp className="w-3.5 h-3.5 text-teal-500" />
                      ) : member.actionTrend === 'down' ? (
                        <ArrowDown className="w-3.5 h-3.5 text-red-400" />
                      ) : null}
                    </div>
                  </td>
                  {/* At Risk */}
                  <td className="px-4 py-4">
                    {member.atRisk ? (
                      <div className="flex items-center gap-1.5" data-testid={`at-risk-${member.id}`}>
                        <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-red-500">{member.atRisk}</span>
                      </div>
                    ) : (
                      <Check className="w-4 h-4 text-teal-500" data-testid={`no-risk-${member.id}`} />
                    )}
                  </td>
                  {/* Chevron */}
                  <td className="px-2 py-4">
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
